/* eslint-disable no-underscore-dangle */
import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  IconButton,
  Typography,
  Pagination,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material'
import DataObjectIcon from '@mui/icons-material/DataObject'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

const LogTableSection = ({
  logs,
  totalCount,
  loading,
  showEventColumn,
  onViewSnapshotClick,
  page,
  perPage,
  pageCount,
  onPageChange,
  onPerPageChange,
}) => (
  <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 960 }}>
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 600, whiteSpace: 'nowrap' } }}>
            <TableCell>Deleted At</TableCell>
            <TableCell>Actor</TableCell>
            <TableCell>Action</TableCell>
            {showEventColumn && <TableCell>Event</TableCell>}
            <TableCell>Bib</TableCell>
            <TableCell>Activity Status</TableCell>
            <TableCell>Activity ID</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell align="right">Snapshot</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && logs.length === 0 && (
            <TableRow>
              <TableCell colSpan={showEventColumn ? 9 : 8} align="center" sx={{ py: 6 }}>
                <CircularProgress size={28} />
              </TableCell>
            </TableRow>
          )}
          {!loading && logs.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={showEventColumn ? 9 : 8}
                align="center"
                sx={{ py: 6, color: 'text.secondary' }}>
                No deletions have been logged yet.
              </TableCell>
            </TableRow>
          )}
          {logs.map((row) => (
            <TableRow key={row._id} hover>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {row.deletedAt ? moment(row.deletedAt).format('DD/MM/YYYY HH:mm:ss') : '-'}
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  {/* actorName is snapshotted, so logs stay readable if the user is removed */}
                  <span>{row.actorName || row.actorId || 'unknown'}</span>
                  {row.actorRole && (
                    <Chip label={row.actorRole} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                  )}
                </Stack>
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Chip
                    label={row.action || 'DELETE_ACTIVITY'}
                    size="small"
                    color="error"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem' }}
                  />
                  {row.deleteConfirmed === false && (
                    <Tooltip title="The delete was logged but never confirmed — verify this record in the database.">
                      <WarningAmberIcon fontSize="small" color="warning" />
                    </Tooltip>
                  )}
                </Stack>
              </TableCell>
              {showEventColumn && (
                <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                  {row.eventId || '-'}
                </TableCell>
              )}
              <TableCell sx={{ fontWeight: 500 }}>{row.bib || '-'}</TableCell>
              <TableCell>{row.activityStatus || '-'}</TableCell>
              <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                {row.activityId || '-'}
              </TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>{row.reason || '-'}</TableCell>
              <TableCell align="right">
                <Tooltip title="View snapshot">
                  <IconButton size="small" onClick={() => onViewSnapshotClick(row)}>
                    <DataObjectIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>

    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1.5,
        alignItems: 'center',
        justifyContent: 'space-between',
        px: { xs: 1.5, sm: 2 },
        py: 1.5,
        borderTop: 1,
        borderColor: 'divider',
      }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {totalCount} total
        </Typography>
        <Select
          size="small"
          value={perPage}
          onChange={(e) => onPerPageChange(Number(e.target.value))}
          sx={{ minWidth: 88 }}>
          {[25, 50, 100].map((n) => (
            <MenuItem key={n} value={n}>
              {n} / page
            </MenuItem>
          ))}
        </Select>
      </Stack>
      <Pagination
        count={pageCount || 1}
        page={page}
        onChange={(e, value) => onPageChange(value)}
        size="small"
        color="primary"
        showFirstButton
        showLastButton
      />
    </Box>
  </Paper>
)

LogTableSection.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  logs: PropTypes.array.isRequired,
  totalCount: PropTypes.number,
  loading: PropTypes.bool,
  showEventColumn: PropTypes.bool,
  onViewSnapshotClick: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  perPage: PropTypes.number.isRequired,
  pageCount: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  onPerPageChange: PropTypes.func.isRequired,
}

export default LogTableSection
